import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import { ArrowBack, OpenInNew } from "@mui/icons-material";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

const BookDetail = () => {
  const { id } = useParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const docRef = doc(db, "entries", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setBook(docSnap.data());
        }
      } catch (error) {
        console.error("Error fetching entry:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!book) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Entry not found</Typography>
        <Button
          component={Link}
          to="/public"
          variant="contained"
          sx={{ mt: 2 }}
        >
          Back to Public Table
        </Button>
      </Box>
    );
  }

  const bd = book.Bibliographical_details || {};

  const paratexts = Object.entries(book.Paratexts || {})
    .filter(([k, v]) => k !== "is_paratext" && k !== "other" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (book.Paratexts?.other) paratexts.push(book.Paratexts.other);

  const genres = Object.entries(book.Genre_subgenre || {})
    .filter(([k, v]) => k !== "other" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (book.Genre_subgenre?.other) genres.push(book.Genre_subgenre.other);

  const summaryKeywords = Object.entries(book.Summary?.keywords || {})
    .filter(([k, v]) => k !== "other" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (book.Summary?.keywords?.other)
    summaryKeywords.push(book.Summary.keywords.other);

  const titleKeywords = Object.entries(book.Title?.title_subtitle_keyword || {})
    .filter(([k, v]) => k !== "other" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (book.Title?.title_subtitle_keyword?.other)
    titleKeywords.push(book.Title.title_subtitle_keyword.other);

  const dedicatees = Array.isArray(book.Dedicatee_s)
    ? book.Dedicatee_s.filter((d) => d.name)
    : [];

  const sourceTitles = [
    book.Source?.main_source_1?.title,
    book.Source?.main_source_2?.title,
    book.Source?.main_source_3?.title,
  ].filter(Boolean);

  const sources = [
    book.Source?.main_source_1,
    book.Source?.main_source_2,
    book.Source?.main_source_3,
  ].filter((s) => s?.title);

  const settingTime = Object.entries(book.Setting?.time || {})
    .filter(([k, v]) => k !== "ancient_time" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (book.Setting?.time?.ancient_time)
    settingTime.push(book.Setting.time.ancient_time);

  const settingPlace = [
    book.Setting?.place?.england && "England",
    book.Setting?.place?.the_moon && "The Moon",
    book.Setting?.place?.europe && `Europe: ${book.Setting.place.europe}`,
    book.Setting?.place?.the_americas &&
      `The Americas: ${book.Setting.place.the_americas}`,
    book.Setting?.place?.africa && `Africa: ${book.Setting.place.africa}`,
    book.Setting?.place?.asia && `Asia: ${book.Setting.place.asia}`,
    book.Setting?.place?.other_worlds_imaginary &&
      `Other: ${book.Setting.place.other_worlds_imaginary}`,
  ].filter(Boolean);

  const socialLoci = Object.entries(book.Setting?.social_loci || {})
    .filter(([, v]) => v === true)
    .map(([k]) => k.replace(/_/g, " "));

  const locationTopoi = Object.entries(book.Setting?.location_topoi || {})
    .filter(([k, v]) => k !== "other" && v === true)
    .map(([k]) => k.replace(/_/g, " "));
  if (book.Setting?.location_topoi?.other)
    locationTopoi.push(book.Setting.location_topoi.other);

  const SectionHeader = ({ children }) => (
    <Grid item xs={12} sx={{ mt: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: "bold", color: "#1976d2" }}>
        {children}
      </Typography>
      <Divider sx={{ mt: 1, mb: 1 }} />
    </Grid>
  );

  const Field = ({ label, value, fullWidth = false }) =>
    value ? (
      <Grid item xs={12} md={fullWidth ? 12 : 6}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
      </Grid>
    ) : null;

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 1000, margin: "0 auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{ color: "#1976d2", fontWeight: "bold" }}
          >
            Text
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              component={Link}
              to="/public"
              variant="outlined"
              startIcon={<ArrowBack />}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Back to Public Table
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <SectionHeader>Author Information</SectionHeader>
          <Field label="Name" value={book.Author?.name} />
          <Field
            label="Gender Attribution"
            value={book.Author?.gender_attribution}
          />
          <Field label="Pseudonym" value={book.Author?.pseudonym} />
          <Field label="Initials" value={book.Author?.initials} />
          <Field label="Attribution" value={book.Author?.attribution} />
          <Field
            label="Source of Attribution"
            value={book.Author?.source_of_attribution}
          />

          <SectionHeader>Title Information</SectionHeader>
          <Field label="Full Title" value={book.Title?.full_title} fullWidth />
          <Field label="Short Version" value={book.Title?.short_version} />
          {titleKeywords.length > 0 && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Title Keywords
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {titleKeywords.join(", ")}
              </Typography>
            </Grid>
          )}

          {sourceTitles.length > 0 && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Title of the source
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {sourceTitles.join("; ")}
              </Typography>
            </Grid>
          )}

          {sources.length > 0 && (
            <>
              <SectionHeader>Source(s)</SectionHeader>
              {sources.map((s, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Source {i + 1}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {[s.title, s.author && `(${s.author})`, s.language, s.genre]
                      .filter(Boolean)
                      .join(" — ")}
                  </Typography>
                </Grid>
              ))}
            </>
          )}

          <SectionHeader>Translation</SectionHeader>
          {book.Translation?.actual_translation && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Translation
              </Typography>
              <Chip label="Yes" size="small" color="success" />
            </Grid>
          )}
          <Field
            label="Translator's name"
            value={book.Translation?.translator_name}
          />
          <Field
            label="Translator's gender"
            value={book.Translation?.translator_gender}
          />
          <Field
            label="Original Title"
            value={book.Translation?.original_title}
          />
          <Field
            label="Original Language"
            value={book.Translation?.original_language}
          />
          {book.Translation?.includes_verse && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Includes Verse
              </Typography>
              <Chip label="Yes" size="small" />
            </Grid>
          )}

          <SectionHeader>Bibliographical Details</SectionHeader>
          <Field
            label="Year of first edition"
            value={bd.year_of_first_edition}
          />
          <Field label="Place of Publication" value={bd.place_of_publication} />
          <Field label="Format" value={bd.format} />
          <Field label="Bookseller" value={bd.bookseller_s} />
          <Field label="Citation number" value={bd.citation_number} />
          <Field label="Printer" value={bd.printer} />
          <Field label="Other Editions" value={bd.other_editions} fullWidth />
          {bd.pdf_url && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                href={bd.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNew />}
              >
                View PDF
              </Button>
            </Grid>
          )}

          {genres.length > 0 && (
            <>
              <SectionHeader>Genre / Subgenre</SectionHeader>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {genres.join(", ")}
                </Typography>
              </Grid>
            </>
          )}

          {paratexts.length > 0 && (
            <>
              <SectionHeader>Paratexts</SectionHeader>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {paratexts.join(", ")}
                </Typography>
              </Grid>
            </>
          )}

          {(settingTime.length > 0 ||
            settingPlace.length > 0 ||
            socialLoci.length > 0 ||
            locationTopoi.length > 0) && (
            <>
              <SectionHeader>Setting</SectionHeader>
              {settingTime.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Time
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {settingTime.join(", ")}
                  </Typography>
                </Grid>
              )}
              {settingPlace.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Place
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {settingPlace.join(", ")}
                  </Typography>
                </Grid>
              )}
              {socialLoci.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Social Loci
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {socialLoci.join(", ")}
                  </Typography>
                </Grid>
              )}
              {locationTopoi.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Location Topoi
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {locationTopoi.join(", ")}
                  </Typography>
                </Grid>
              )}
            </>
          )}

          {(summaryKeywords.length > 0 || book.Summary?.short_summary) && (
            <>
              <SectionHeader>Summary</SectionHeader>
              {summaryKeywords.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Keywords
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {summaryKeywords.join(", ")}
                  </Typography>
                </Grid>
              )}
              <Field
                label="Short Summary"
                value={book.Summary?.short_summary}
                fullWidth
              />
            </>
          )}

          {dedicatees.length > 0 && (
            <>
              <SectionHeader>Dedicatees</SectionHeader>
              {dedicatees.map((d, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Dedicatee {i + 1}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {d.name}
                    {d.gender ? ` (${d.gender})` : ""}
                  </Typography>
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default BookDetail;
